import React from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { AggregatedEvent } from '../../types/calendarTypes';
import { getStatusColor } from '../../utils/calendarUtils';

interface MobileMonthViewProps {
    date: Date;
    events: AggregatedEvent[];
    onSelectDay: (date: Date) => void;
}

const MAX_DOTS = 4;

// Mobile-only replacement for react-big-calendar's native month grid. Cramming
// full event chips (skill, hours, status) into ~50px-wide cells is unreadable
// on a phone, so instead each day just shows its number plus small colored
// dots summarizing shift statuses present that day. Tapping a day drills into
// the day view, which already shows full shift details legibly.
const MobileMonthView: React.FC<MobileMonthViewProps> = ({ date, events, onSelectDay }) => {
    const monthStart = moment(date).startOf('month');
    const gridStart = monthStart.clone().startOf('week');
    const gridEnd = moment(date).endOf('month').endOf('week');
    const today = moment();

    const weeks: moment.Moment[][] = [];
    const cursor = gridStart.clone();
    while (cursor.isSameOrBefore(gridEnd)) {
        const week: moment.Moment[] = [];
        for (let i = 0; i < 7; i++) {
            week.push(cursor.clone());
            cursor.add(1, 'day');
        }
        weeks.push(week);
    }

    const eventsByDay = new Map<string, AggregatedEvent[]>();
    events.forEach((e) => {
        const key = moment(e.start).format('YYYY-MM-DD');
        const list = eventsByDay.get(key) ?? [];
        list.push(e);
        eventsByDay.set(key, list);
    });

    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e0e0e0' }}>
                {dayLabels.map((label) => (
                    <Typography
                        key={label}
                        variant="caption"
                        sx={{ textAlign: 'center', py: 0.75, color: '#CC0000', fontWeight: 700 }}
                    >
                        {label}
                    </Typography>
                ))}
            </Box>

            {weeks.map((week, wIdx) => (
                <Box key={wIdx} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #f0f0f0' }}>
                    {week.map((day) => {
                        const inCurrentMonth = day.isSame(monthStart, 'month');
                        const isToday = day.isSame(today, 'day');
                        const dayEvents = eventsByDay.get(day.format('YYYY-MM-DD')) ?? [];
                        const statuses = Array.from(new Set(dayEvents.map((e) => e.resource.status)));
                        const visibleStatuses = statuses.slice(0, MAX_DOTS);
                        const extraCount = statuses.length - visibleStatuses.length;

                        return (
                            <Box
                                key={day.format('YYYY-MM-DD')}
                                onClick={() => onSelectDay(day.toDate())}
                                sx={{
                                    minHeight: 56,
                                    p: 0.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    cursor: 'pointer',
                                    backgroundColor: isToday ? '#fff5f5' : 'transparent',
                                    opacity: inCurrentMonth ? 1 : 0.35,
                                    '&:active': { backgroundColor: '#f5f5f5' },
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: isToday ? 700 : 500,
                                        color: isToday ? '#CC0000' : 'text.primary',
                                        fontSize: '0.85rem',
                                        mt: 0.5,
                                    }}
                                >
                                    {day.format('D')}
                                </Typography>
                                {dayEvents.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.4, mt: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        {visibleStatuses.map((status) => (
                                            <Box
                                                key={status}
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    backgroundColor: getStatusColor(status),
                                                }}
                                            />
                                        ))}
                                        {extraCount > 0 && (
                                            <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', lineHeight: 1 }}>
                                                +{extraCount}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
};

export default MobileMonthView;